/**
 * Vorschau und Veröffentlichen (10-web-admin § 7).
 *
 * The screen where publishing takes somebody's desk away. Two rules it exists
 * to enforce: every conflict must be answered before the button works, and a
 * duplicate label is the one warning that blocks — because two stickers
 * claiming the same desk is not something anyone downstream can resolve.
 *
 * Laid out as the board draws it: the member's preview in a phone frame on the
 * left, with the checklist beside it; the changes and the conflicts on the
 * right in a 460 px column. The preview renders the shared `FloorPlan` with the
 * draft's own data, so what staff see is what members will get (§ 6.1).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router';
import { Button, FloorPlan, LoadingScreen, StateScreen } from '@cowoo/shared/ui-web';
import {
  canPublish,
  formatDayShort,
  newIdempotencyKey,
  publishWarnings,
  todayBerlin,
  type PublishWarning,
} from '@cowoo/shared';
import { api } from '../../lib/api';
import { Dialog, EditorBar, Icon, Segmented } from '../../ui';
import { toast } from '../../ui/toast';

/**
 * The diff's rows. The API types this loosely — the union has seven shapes and
 * only `kind` is common — so the screen narrows it here rather than pretending
 * every row carries every field.
 */
interface Change {
  kind: string;
  resourceId?: string;
  label?: string;
  from?: string | null;
  to?: string | null;
}

interface Conflict {
  bookingId: string;
  resourceId: string;
  day: string;
  reason: 'removed' | 'blocked';
  suggestions: string[];
}

type PreviewMode = 'app' | 'web' | 'terminal';

export function Publish() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { planId = '' } = useParams();
  const [mode, setMode] = useState<PreviewMode>('app');

  /** `bookingId -> resourceId`; a conflict is answered by moving the booking … */
  const [resolutions, setResolutions] = useState<Record<string, string>>({});
  /** … or by dropping the change that caused it from this version. */
  const [reverted, setReverted] = useState<string[]>([]);
  /** § 7 "Anderen Platz …" — the conflict whose free desks are being listed. */
  const [picking, setPicking] = useState<Conflict | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  const plan = useQuery({
    queryKey: ['admin', 'floor-plans'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plans');
      if (error) throw error;
      return data.items;
    },
    select: (items) => items.find((item) => item.id === planId) ?? null,
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });

  /**
   * The version being published is the plan's draft — read from the plan, not
   * from a `?version=` parameter that a direct link does not carry. A plan
   * without a draft has nothing to publish, and says so.
   */
  const versionId = plan.data?.draft?.id ?? '';
  const locationId = plan.data?.locationId ?? '';
  const version = plan.data?.draft?.version ?? 0;
  const previous = plan.data?.published?.version ?? 0;
  const locationName = locations.data?.find((item) => item.id === locationId)?.name ?? '';
  const planName = t('admin.plans.cardName', { location: locationName, floor: plan.data?.name ?? '' });

  const preview = useQuery({
    queryKey: ['admin', 'plan-preview', versionId],
    enabled: versionId !== '',
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plan-versions/{vid}/preview', {
        params: { path: { vid: versionId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const diff = useQuery({
    queryKey: ['admin', 'plan-diff', versionId],
    enabled: versionId !== '',
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plan-versions/{vid}/diff', {
        params: { path: { vid: versionId } },
      });
      if (error) throw error;
      return (data?.changes ?? []) as unknown as Change[];
    },
  });

  const conflicts = useQuery({
    queryKey: ['admin', 'plan-conflicts', versionId],
    enabled: versionId !== '' && locationId !== '',
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plan-versions/{vid}/conflicts', {
        params: { path: { vid: versionId }, query: { locationId } },
      });
      if (error) throw error;
      return (data?.conflicts ?? []) as Conflict[];
    },
  });

  const publish = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/admin/floor-plan-versions/{vid}/publish', {
        params: { path: { vid: versionId }, header: { 'idempotency-key': newIdempotencyKey() } },
        body: {
          locationId,
          resolutions: Object.entries(resolutions).map(([bookingId, toResourceId]) => ({
            bookingId,
            toResourceId,
          })),
          revert: reverted,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'floor-plans'] });
      // § 7 — "Version {{n}} ist live." with the sticker offer; the stickers of
      // the version just published are now the published ones.
      toast(t('admin.publish.done', { version }), {
        tone: 'success',
        action: {
          label: t('admin.publish.printStickers'),
          onPress: () => void navigate(`/grundrisse/${planId}/aufkleber`),
        },
      });
      void navigate('/grundrisse');
    },
  });

  /**
   * § 7 — the picker lists what is free on the booking's day, from the same
   * availability the member's picker reads. The desks the server already
   * suggested are left out: they are chips in the card already.
   */
  const freeDesks = useQuery({
    queryKey: ['admin', 'availability', locationId, picking?.day],
    enabled: picking !== null && locationId !== '',
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/availability', {
        params: { path: { id: locationId }, query: { day: picking?.day } },
      });
      if (error) throw error;
      return 'desks' in data ? data.desks : [];
    },
    select: (desks) =>
      desks.filter(
        (desk) =>
          desk.state === 'free' &&
          desk.resourceId !== picking?.resourceId &&
          !(picking?.suggestions ?? []).includes(desk.resourceId),
      ),
  });

  const placements = useMemo(() => preview.data?.placements ?? [], [preview.data]);
  const labelOf = (resourceId: string): string =>
    placements.find((placement) => placement.resourceId === resourceId)?.label ?? resourceId.slice(0, 6);
  const zoneOf = (resourceId: string): string | null =>
    placements.find((placement) => placement.resourceId === resourceId)?.zone ?? null;

  // § 7 — "desks without zone": a room has no zone by nature, so the warning
  // is kept to desks; the duplicate-label check stays across every kind,
  // because the server's uniqueness is per version, not per kind.
  const warnings: PublishWarning[] = useMemo(
    () =>
      publishWarnings(placements, { pxPerCm: preview.data?.pxPerCm ?? null }).filter(
        (warning) =>
          warning.kind !== 'no_zone' ||
          placements.some((placement) => placement.kind === 'desk' && placement.label === warning.label),
      ),
    [placements, preview.data?.pxPerCm],
  );

  const open = (conflicts.data ?? []).filter(
    (conflict) => !(conflict.bookingId in resolutions) && !reverted.includes(conflict.resourceId),
  );
  const changes = diff.data ?? [];
  const ready = canPublish({ warnings, unresolvedConflicts: open.length, changeCount: changes.length });

  const desks = placements.filter((placement) => placement.kind === 'desk');
  const added = changes.filter((change) => change.kind === 'added');
  const newIds = new Set(added.map((change) => change.resourceId));
  const sample = desks.find((desk) => newIds.has(desk.resourceId)) ?? desks[0] ?? null;

  if (plan.isLoading) return <LoadingScreen label={t('common.loading')} />;
  if (plan.data && !plan.data.draft) {
    return <StateScreen title={t('admin.publish.noDraft')} />;
  }

  return (
    <div className="bg-fog flex h-[calc(100vh-64px)] flex-col">
      <EditorBar
        onBack={() => void navigate(`/grundrisse/${planId}/editor`)}
        title={t('admin.publish.title', { plan: planName })}
        subline={t('admin.publish.sub', { version, previous })}
        actions={
          <>
            <Button
              size="md"
              variant="secondary"
              onClick={() => void navigate(`/grundrisse/${planId}/editor`)}
            >
              {t('admin.publish.back')}
            </Button>
            <Button
              size="md"
              disabled={!ready}
              loading={publish.isPending}
              onClick={() => publish.mutate()}
              // § 7 — disabled at 50 % while unresolved conflicts exist.
              className={ready ? '' : '!bg-teal !text-on-teal opacity-50'}
            >
              {open.length > 0 ? t('admin.publish.rebookAndPublish') : t('admin.publish.submit', { version })}
            </Button>
          </>
        }
      />

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_460px] gap-6 p-6">
        {/* ------------------------- Vorschau als Mitglied ------------------------ */}
        <section className="border-line rounded-card flex min-h-0 flex-col gap-3 border bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="font-heading text-ink text-base font-bold">{t('admin.publish.previewTitle')}</p>
            <Segmented
              size="sm"
              label={t('admin.publish.previewTitle')}
              value={mode}
              onChange={setMode}
              options={[
                { value: 'app', label: t('admin.publish.modeApp') },
                { value: 'web', label: t('admin.publish.modeWeb') },
                { value: 'terminal', label: t('admin.publish.modeTerminal') },
              ]}
            />
          </div>

          <div className="flex min-h-0 flex-1 items-center justify-center gap-10 overflow-hidden">
            {mode === 'terminal' ? (
              <div className="border-line rounded-card flex h-[520px] w-[300px] flex-col items-center justify-center gap-3 border bg-[#151515] p-6 text-center text-white">
                <p className="font-heading text-lg font-bold">
                  {sample
                    ? t('admin.publish.terminalSample', { label: sample.label, zone: sample.zone ?? '' })
                    : '—'}
                </p>
                <p className="text-[13px] opacity-80">{t('admin.publish.terminalNote')}</p>
              </div>
            ) : (
              <div
                className={`relative overflow-hidden bg-white ${
                  mode === 'app'
                    ? 'border-ink h-[620px] w-[390px] rounded-[32px] border-8 shadow-[0_20px_50px_rgba(21,21,21,0.18)]'
                    : 'border-line rounded-card h-[520px] w-[640px] border'
                }`}
              >
                <div className="flex flex-col gap-2.5 p-5 pt-9">
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-ink text-base font-bold">
                      {t('admin.publish.pickDesk')}
                    </span>
                    <span className="bg-teal-tint text-teal-dark inline-flex h-7 items-center rounded-full px-2.5 text-xs font-semibold">
                      {formatDayShort(todayBerlin())}
                    </span>
                  </div>
                  <div
                    className={
                      mode === 'app' ? 'max-h-[400px] overflow-hidden' : 'max-h-[380px] overflow-hidden'
                    }
                  >
                    <FloorPlan
                      objects={desks}
                      stateOf={(id) =>
                        placements.find((p) => p.resourceId === id)?.status === 'blocked' ? 'blocked' : 'free'
                      }
                      t={t}
                      backgroundUrl={preview.data?.imageUrl}
                      backgroundOpacity={preview.data?.backgroundOpacity}
                    />
                  </div>
                </div>
                {sample ? (
                  <div className="border-line absolute inset-x-0 bottom-0 flex flex-col gap-2 border-t bg-white px-4 pb-5 pt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-ink text-sm font-bold">
                          {t('admin.editor.rowDesk', { label: sample.label, zone: sample.zone ?? '' })}
                        </p>
                        <p className="text-muted text-xs">
                          {newIds.has(sample.resourceId)
                            ? t('admin.publish.newInVersion', { version })
                            : t('admin.publish.unchanged')}
                        </p>
                      </div>
                      <span className="bg-teal-tint text-teal-dark inline-flex h-[26px] items-center rounded-full px-2.5 text-xs font-semibold">
                        {t('plan.legend.free')}
                      </span>
                    </div>
                    <div className="bg-teal text-on-teal flex h-11 items-center justify-center rounded-xl text-sm font-semibold">
                      {t('admin.publish.reserveSample', { label: sample.label })}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            <div className="flex max-w-[300px] flex-col gap-3">
              <p className="text-text text-sm leading-normal">{t('admin.publish.previewCopy')}</p>
              <ul className="text-text flex flex-col gap-2 text-[13px]">
                <Check>
                  {t('admin.publish.checkBookable', { count: desks.length, added: added.length })}
                </Check>
                <Check>{t('admin.publish.checkRoom')}</Check>
                <li>
                  <button
                    type="button"
                    onClick={() => void navigate(`/grundrisse/${planId}/aufkleber`)}
                    className="text-teal-dark flex items-center gap-2 underline"
                  >
                    <Icon name="check" size={16} className="text-teal" />
                    {t('admin.publish.checkStickers', { count: added.length })}
                  </button>
                </li>
                {warnings.map((warning, index) => (
                  <li
                    key={index}
                    className={`flex items-center gap-2 ${warning.blocking ? 'text-danger-text' : 'text-warn-text'}`}
                  >
                    <Icon name="warn" size={16} />
                    {warningText(warning, t)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ------------------------------ right column ----------------------------- */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
          <section className="border-line rounded-card flex flex-col gap-2.5 border bg-white px-5 py-[18px]">
            <p className="font-heading text-ink text-base font-bold">
              {t('admin.publish.changes', { version })}
            </p>
            {changes.length === 0 ? (
              <p className="text-muted text-sm">{t('admin.publish.noChanges', { previous })}</p>
            ) : (
              <div className="flex flex-col text-sm">
                {groupChanges(changes).map(([kind, rows]) => (
                  <div
                    key={kind}
                    className="border-hairline flex justify-between gap-3 border-b py-2 last:border-b-0"
                  >
                    <span className="text-ink">
                      {t(`admin.publish.change.${kind}`, { count: rows.length })}
                    </span>
                    <span className="text-muted text-right">{describe(kind, rows, labelOf, zoneOf)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {(conflicts.data ?? []).length > 0 ? (
            <section className="border-warn-dot rounded-card flex flex-col gap-3 border-2 bg-white px-5 py-[18px]">
              <div className="flex items-center gap-2.5">
                <span className="bg-warn-bg text-warn-text rounded-input flex h-9 w-9 flex-none items-center justify-center">
                  <Icon name="warn" size={18} />
                </span>
                <div>
                  <p className="text-ink text-[15px] font-bold">
                    {t('admin.publish.conflictsTitle', { count: conflicts.data?.length ?? 0 })}
                  </p>
                  <p className="text-muted text-xs">
                    {[...new Set((conflicts.data ?? []).map((c) => c.resourceId))]
                      .map((id) =>
                        t('admin.publish.conflictCause', {
                          label: labelOf(id),
                          reason: t(
                            conflicts.data?.find((c) => c.resourceId === id)?.reason === 'removed'
                              ? 'admin.publish.reasonRemoved'
                              : 'admin.publish.reasonBlocked',
                          ),
                        }),
                      )
                      .join(' · ')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {(conflicts.data ?? []).map((conflict) => (
                  <div
                    key={conflict.bookingId}
                    className="border-line flex flex-col gap-2 rounded-xl border p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-ink text-sm font-semibold">
                          {t('admin.publish.conflictBooking', { day: formatDayShort(conflict.day) })}
                        </p>
                        <p className="text-muted text-xs">{t('admin.publish.conflictKind')}</p>
                      </div>
                      <span className="bg-teal-tint text-teal-dark inline-flex h-[26px] items-center rounded-full px-2.5 text-xs font-semibold">
                        {t('admin.editor.panelDeskTitle', { label: labelOf(conflict.resourceId) })}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[13px]">
                      <span className="text-muted">{t('admin.publish.rebookTo')}</span>
                      {/* The server ranks these, same zone first; the first is
                          the one a person would have picked anyway. */}
                      {[
                        ...conflict.suggestions,
                        // A desk picked by hand joins the row as a chip, so the
                        // choice stays visible after the dialog closes.
                        ...(resolutions[conflict.bookingId] &&
                        !conflict.suggestions.includes(resolutions[conflict.bookingId]!)
                          ? [resolutions[conflict.bookingId]!]
                          : []),
                      ].map((suggestion) => {
                        const chosen = resolutions[conflict.bookingId] === suggestion;
                        return (
                          <button
                            key={suggestion}
                            type="button"
                            aria-pressed={chosen}
                            onClick={() =>
                              setResolutions((current) => ({ ...current, [conflict.bookingId]: suggestion }))
                            }
                            className={`inline-flex h-7 items-center rounded-full px-2.5 font-semibold ${
                              chosen ? 'bg-teal text-white' : 'border-line text-ink border bg-white'
                            }`}
                          >
                            {t('admin.editor.panelDeskTitle', { label: labelOf(suggestion) })}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          setPicked(null);
                          setPicking(conflict);
                        }}
                        className="border-line text-teal-dark inline-flex h-7 items-center rounded-full border bg-white px-2.5 font-semibold"
                      >
                        {t('admin.publish.otherDesk')}
                      </button>
                      <button
                        type="button"
                        aria-pressed={reverted.includes(conflict.resourceId)}
                        onClick={() =>
                          setReverted((current) =>
                            current.includes(conflict.resourceId)
                              ? current.filter((id) => id !== conflict.resourceId)
                              : [...current, conflict.resourceId],
                          )
                        }
                        className={`inline-flex h-7 items-center rounded-full px-2.5 font-semibold ${
                          reverted.includes(conflict.resourceId)
                            ? 'bg-ink text-white'
                            : 'border-line text-muted border border-dashed bg-white'
                        }`}
                      >
                        {t('admin.publish.leaveOpen', { label: labelOf(conflict.resourceId) })}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-muted text-xs leading-normal">
                {t('admin.publish.conflictRule', { version })}
              </p>
            </section>
          ) : null}
        </div>
      </div>

      {picking ? (
        <Dialog
          title={t('admin.publish.otherDeskTitle', { day: formatDayShort(picking.day) })}
          intro={t('admin.publish.otherDeskHint')}
          confirmLabel={t('admin.publish.rebookTo')}
          disabled={picked === null}
          onConfirm={() => {
            if (picked) {
              setResolutions((current) => ({ ...current, [picking.bookingId]: picked }));
            }
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        >
          {freeDesks.data && freeDesks.data.length === 0 ? (
            <p className="text-adminSmall text-muted">{t('admin.publish.otherDeskEmpty')}</p>
          ) : (
            <div role="radiogroup" className="flex max-h-[320px] flex-wrap gap-2 overflow-y-auto">
              {(freeDesks.data ?? []).map((desk) => {
                const chosen = picked === desk.resourceId;
                return (
                  <button
                    key={desk.resourceId}
                    type="button"
                    role="radio"
                    aria-checked={chosen}
                    onClick={() => setPicked(desk.resourceId)}
                    className={`inline-flex h-8 items-center rounded-full px-3 text-[13px] font-semibold ${
                      chosen ? 'bg-teal text-white' : 'border-line text-ink border bg-white'
                    }`}
                  >
                    {t('admin.editor.rowDesk', { label: desk.label, zone: desk.zone ?? '' })}
                  </button>
                );
              })}
            </div>
          )}
        </Dialog>
      ) : null}
    </div>
  );
}

function Check({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      <Icon name="check" size={16} className="text-teal" />
      {children}
    </li>
  );
}

/** § 7 — the diff is shown grouped ("3 Plätze hinzugefügt"), not row by row. */
export function groupChanges(changes: readonly Change[]): Array<[string, Change[]]> {
  const groups = new Map<string, Change[]>();
  for (const change of changes) groups.set(change.kind, [...(groups.get(change.kind) ?? []), change]);
  return [...groups.entries()];
}

/** "23, 24, 25 · Insel 4" — the labels of a group, and their zone if it is one. */
function describe(
  kind: string,
  rows: Change[],
  labelOf: (id: string) => string,
  zoneOf: (id: string) => string | null,
): string {
  if (kind === 'relabelled') {
    return rows.map((row) => `${row.from ?? ''} → ${row.to ?? ''}`).join(', ');
  }
  const labels = rows
    .map((row) => row.label ?? (row.resourceId ? labelOf(row.resourceId) : ''))
    .filter(Boolean);
  const zones = new Set(rows.map((row) => (row.resourceId ? zoneOf(row.resourceId) : null)).filter(Boolean));
  const zone = zones.size === 1 ? [...zones][0] : null;
  return zone ? `${labels.join(', ')} · ${zone}` : labels.join(', ');
}

function warningText(
  warning: PublishWarning,
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  switch (warning.kind) {
    case 'duplicate_label':
      return t('admin.publish.warning.duplicate_label', { label: warning.label });
    case 'overlap':
      return t('admin.publish.warning.overlap', { a: warning.labels[0], b: warning.labels[1] });
    case 'no_zone':
      return t('admin.publish.warning.no_zone', { label: warning.label });
    default:
      return t('admin.publish.warning.no_scale');
  }
}
